import React, { useState, useEffect } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { shortKey, formatCurrency, lamportsToAmount, normalizeLamports } from '../../utils/format';
import { useBalances } from '../../wallet/useBalances';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  useCloseableMatches,
  requestCloseIntent,
  confirmClose,
  SerializedInstruction,
  MatchDetails,
  useOrphanedEscrows,
  requestOrphanedCloseIntent,
  OrphanedEscrow,
} from '../../api/matches';
import { useClusterStore } from '../../state/clusterStore';
import { sendViaRpc } from '../../wallet/sendViaRpc';
import { useSolPrice } from '../../api/prices';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Transaction, PublicKey, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { Currency } from '@solante/shared';

// Animated balance counter
const AnimatedBalance: React.FC<{
  value: number;
  currency: string;
  large?: boolean;
}> = ({ value, currency, large }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated || value === 0) {
      setDisplayValue(value);
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / 1500, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(eased * value);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setHasAnimated(true);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, hasAnimated]);

  const formatted = displayValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: currency === 'SOL' ? 4 : 2,
  });

  return (
    <span
      className={
        large ? 'font-display text-3xl font-bold sm:text-4xl' : 'font-arcade text-xl sm:text-2xl'
      }
    >
      {formatted}
    </span>
  );
};

// Connection pulse indicator
const ConnectionPulse: React.FC<{ connected: boolean }> = ({ connected }) => (
  <span className="relative flex h-3 w-3">
    {connected && (
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-consumable opacity-75" />
    )}
    <span
      className={`relative inline-flex h-3 w-3 rounded-full ${
        connected ? 'bg-consumable' : 'bg-slate-500'
      }`}
    />
  </span>
);

export const WalletPage: React.FC = () => {
  const { publicKey, connected, disconnect, wallet, signTransaction } = useWallet();
  const { connection } = useConnection();
  const walletAddress = publicKey?.toBase58();
  const { data: balances, isLoading } = useBalances(walletAddress);
  const { data: closeableMatches } = useCloseableMatches(walletAddress);
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [reclaimingMatchId, setReclaimingMatchId] = useState<string | null>(null);
  const [reclaimingOrphanedAccount, setReclaimingOrphanedAccount] = useState<string | null>(null);
  const cluster = useClusterStore((s) => s.cluster);
  const {
    data: orphanedEscrows,
    refetch: scanOrphanedEscrows,
    isFetching: isScanning,
  } = useOrphanedEscrows(walletAddress, cluster);

  const copyAddress = async () => {
    if (!walletAddress) return;
    await navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    toast.success('Address copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const reclaimRentMutation = useMutation({
    mutationFn: async (match: MatchDetails) => {
      if (!publicKey || !walletAddress) {
        throw new Error('Wallet not connected');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      setReclaimingMatchId(match.matchId);
      const reclaimToastId = toast.loading('Step 1/3: Preparing rent reclaim…');

      try {
        const intent = await requestCloseIntent(match.matchId, walletAddress);

        toast.loading('Step 2/3: Approve the transaction in your wallet.', {
          id: reclaimToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/3: Confirming on-chain…', {
          id: reclaimToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        // Notify server to remove match from closeable list
        await confirmClose(match.matchId, walletAddress, signature);

        toast.success('Rent reclaimed! ~0.004 SOL returned to your wallet.', {
          id: reclaimToastId,
          duration: 5000,
        });

        return { matchId: match.matchId, signature };
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to reclaim rent. Please try again.');
        toast.error(normalizedError.message, { id: reclaimToastId });
        throw normalizedError;
      } finally {
        setReclaimingMatchId(null);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches', 'closeable'] });
    },
  });

  const reclaimOrphanedMutation = useMutation({
    mutationFn: async (orphan: OrphanedEscrow) => {
      if (!publicKey || !walletAddress) {
        throw new Error('Wallet not connected');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      setReclaimingOrphanedAccount(orphan.matchAccount);
      const reclaimToastId = toast.loading('Step 1/3: Preparing rent reclaim…');

      try {
        const intent = await requestOrphanedCloseIntent(
          orphan.matchAccount,
          orphan.escrowAccount,
          walletAddress,
          cluster,
        );

        toast.loading('Step 2/3: Approve the transaction in your wallet.', {
          id: reclaimToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/3: Confirming on-chain…', {
          id: reclaimToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        toast.success('Rent reclaimed! ~0.004 SOL returned to your wallet.', {
          id: reclaimToastId,
          duration: 5000,
        });

        return { matchAccount: orphan.matchAccount, signature };
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to reclaim rent. Please try again.');
        toast.error(normalizedError.message, { id: reclaimToastId });
        throw normalizedError;
      } finally {
        setReclaimingOrphanedAccount(null);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches', 'orphaned'] });
    },
  });

  const handleScanOrphaned = () => {
    toast.promise(scanOrphanedEscrows(), {
      loading: 'Scanning blockchain for lost escrows…',
      success: (result) => {
        const count = result.data?.length ?? 0;
        return count > 0
          ? `Found ${count} orphaned escrow${count > 1 ? 's' : ''} to reclaim!`
          : 'No orphaned escrows found.';
      },
      error: 'Failed to scan blockchain. Please try again.',
    });
  };

  const { data: solPrice = 0 } = useSolPrice();
  const totalValue = (balances?.sol ?? 0) * solPrice + (balances?.usdc ?? 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-4xl space-y-8 py-8"
    >
      {/* Header */}
      <header>
        <h1 className="neon-text font-display text-3xl font-bold">Wallet</h1>
      </header>

      {/* Main Balance Card */}
      <section className="relative overflow-hidden rounded-2xl border border-joker/30 bg-gradient-to-br from-joker/10 via-transparent to-arcade-purple/10 p-5 sm:p-8">
        <div className="relative">
          {/* Connection Status */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ConnectionPulse connected={connected} />
              <span
                className={`text-sm font-medium ${connected ? 'text-consumable' : 'text-slate-400'}`}
              >
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {connected && wallet && (
              <div className="flex items-center gap-2">
                <span className="font-arcade text-xs text-white/50">{wallet.adapter.name}</span>
              </div>
            )}
          </div>

          {connected ? (
            <>
              {/* Total Balance */}
              <div className="mb-2">
                <p className="text-xs uppercase tracking-[0.2em] text-white/40">Total Balance</p>
                <div className="mt-1 flex items-baseline gap-2">
                  {isLoading ? (
                    <div className="h-10 w-48 animate-pulse rounded bg-white/10" />
                  ) : (
                    <div className="neon-text-gold flex items-baseline gap-1">
                      <span className="font-display text-2xl font-bold">$</span>
                      <AnimatedBalance value={totalValue} currency="USD" large />
                    </div>
                  )}
                </div>
              </div>

              {/* Wallet Address */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30">Address</p>
                  <p className="font-mono text-sm text-white/70">
                    {walletAddress ? shortKey(walletAddress) : '—'}
                  </p>
                </div>
                <button
                  onClick={copyAddress}
                  className="btn-press rounded-lg border border-white/20 bg-white/5 p-2 transition hover:bg-white/10"
                  title="Copy address"
                >
                  <AnimatePresence mode="wait">
                    {copied ? (
                      <motion.svg
                        key="check"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="h-4 w-4 text-consumable"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </motion.svg>
                    ) : (
                      <motion.svg
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="h-4 w-4 text-white/60"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </motion.svg>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-white/5">
                <svg
                  className="h-8 w-8 text-white/30"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <p className="font-arcade text-lg text-white/50">
                Connect your wallet to view balance
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Token Balances */}
      {connected && (
        <section className="grid gap-4 md:grid-cols-2">
          {/* SOL Balance */}
          <div className="group arcade-panel rounded-xl p-4 sm:p-6 transition hover:border-arcade-cyan/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-700">
                    <span className="font-display text-xs font-bold text-white">SOL</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Solana</p>
                    <p className="text-xs text-white/40">Native Token</p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                {isLoading ? (
                  <div className="h-7 w-24 animate-pulse rounded bg-white/10" />
                ) : (
                  <div className="text-white">
                    <AnimatedBalance value={balances?.sol ?? 0} currency="SOL" />
                    <span className="ml-1 text-sm text-white/50">SOL</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* USDC Balance */}
          <div className="group arcade-panel rounded-xl p-4 sm:p-6 transition hover:border-consumable/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700">
                    <span className="font-display text-[10px] font-bold text-white">USDC</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">USD Coin</p>
                    <p className="text-xs text-white/40">Stablecoin</p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                {isLoading ? (
                  <div className="h-7 w-24 animate-pulse rounded bg-white/10" />
                ) : (
                  <div className="text-white">
                    <AnimatedBalance value={balances?.usdc ?? 0} currency="USDC" />
                    <span className="ml-1 text-sm text-white/50">USDC</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reclaimable Rent Section */}
      {connected && closeableMatches && closeableMatches.length > 0 && (
        <section className="rounded-xl border border-joker/30 bg-joker/5 p-6">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-joker/20">
              <svg
                className="h-4 w-4 text-joker"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-white">Reclaimable Rent</h3>
              <p className="text-xs text-white/50">
                You have {closeableMatches.length} completed match
                {closeableMatches.length > 1 ? 'es' : ''} with reclaimable rent (~0.004 SOL each)
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {closeableMatches.map((match) => {
              const wagerAmount = lamportsToAmount(
                normalizeLamports(match.wager.amountLamports),
                match.wager.currency as Currency,
              );
              const isReclaiming = reclaimingMatchId === match.matchId;
              return (
                <div
                  key={match.matchId}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white/80 truncate">
                      Match {shortKey(match.matchId)}
                    </p>
                    <p className="text-xs text-white/50">
                      {formatCurrency(wagerAmount, match.wager.currency as Currency)} •{' '}
                      {match.status === 'completed' ? 'Completed' : 'Cancelled'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => reclaimRentMutation.mutate(match)}
                    disabled={reclaimRentMutation.isPending}
                    className="btn-press rounded-lg border border-joker/50 bg-joker/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-joker transition hover:bg-joker/20 disabled:opacity-50"
                  >
                    {isReclaiming ? 'Reclaiming…' : 'Reclaim'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Recover Lost Rent Section */}
      {connected && (
        <section className="rounded-xl border border-arcade-cyan/30 bg-arcade-cyan/5 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-arcade-cyan/20">
                <svg
                  className="h-4 w-4 text-arcade-cyan"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-white">Recover Lost Rent</h3>
                <p className="text-xs text-white/50">
                  Scan for orphaned escrows from matches lost due to server restarts
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleScanOrphaned}
              disabled={isScanning}
              className="btn-press rounded-lg border border-arcade-cyan/50 bg-arcade-cyan/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-arcade-cyan transition hover:bg-arcade-cyan/20 disabled:opacity-50"
            >
              {isScanning ? 'Scanning…' : 'Scan Blockchain'}
            </button>
          </div>

          {/* Show orphaned escrows if any were found */}
          {orphanedEscrows && orphanedEscrows.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs text-arcade-cyan/70">
                Found {orphanedEscrows.length} orphaned escrow
                {orphanedEscrows.length > 1 ? 's' : ''} (~0.004 SOL each)
              </p>
              {orphanedEscrows.map((orphan) => {
                const isReclaiming = reclaimingOrphanedAccount === orphan.matchAccount;
                const dateStr = new Date(orphan.createdAt).toLocaleDateString();
                return (
                  <div
                    key={orphan.matchAccount}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white/80">
                        {orphan.matchAccount.slice(0, 8)}…{orphan.matchAccount.slice(-8)}
                      </p>
                      <p className="text-xs text-white/50">
                        {orphan.status === 'settled' ? 'Settled' : 'Cancelled'} • {dateStr}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => reclaimOrphanedMutation.mutate(orphan)}
                      disabled={reclaimOrphanedMutation.isPending}
                      className="btn-press rounded-lg border border-arcade-cyan/50 bg-arcade-cyan/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-arcade-cyan transition hover:bg-arcade-cyan/20 disabled:opacity-50"
                    >
                      {isReclaiming ? 'Reclaiming…' : 'Reclaim'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Show message if scan completed but found nothing */}
          {orphanedEscrows && orphanedEscrows.length === 0 && (
            <p className="text-center text-xs text-white/40">
              No orphaned escrows found. All your rent is accounted for.
            </p>
          )}
        </section>
      )}

      {/* Quick Actions */}
      {connected && (
        <section className="flex flex-wrap items-center justify-center gap-4">
          <a
            href="/matchmaking"
            className="btn-press flex items-center gap-2 rounded-xl border border-arcade-cyan/50 bg-arcade-cyan/10 px-6 py-3 font-arcade text-sm text-arcade-cyan transition hover:bg-arcade-cyan/20"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Play Now
          </a>

          <button
            onClick={() => disconnect()}
            className="btn-press flex items-center gap-2 rounded-xl border border-danger/50 bg-danger/10 px-6 py-3 font-arcade text-sm text-danger transition hover:bg-danger/20"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Disconnect
          </button>
        </section>
      )}

      {/* Footer Note */}
      <footer className="pt-8 text-center">
        <p className="text-xs text-white/30">
          Wagers are held in secure escrow smart contracts on Solana
        </p>
      </footer>
    </motion.div>
  );
};
